import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogIn, LogOut, Clock, Undo2, ClipboardList, AlertCircle, Save, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ApprovalsPage from "@/pages/ApprovalsPage";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const statusColor = (s) => {
  if (s === "present") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "half_day") return "bg-amber-50 text-amber-700 border-amber-200";
  if (s === "absent") return "bg-red-50 text-red-700 border-red-200";
  return "bg-zinc-100 text-zinc-600 border-zinc-200";
};

function timeIso(iso) { return iso ? dayjs(iso).format("HH:mm") : "—"; }

export default function AttendancePage({ embedded = false }) {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [my, setMy] = useState([]);
  const [tg, setTg] = useState("");
  const [tc, setTc] = useState("");
  const [busy, setBusy] = useState(false);
  const [undoLeft, setUndoLeft] = useState(0);
  const isMgr = user?.role === "admin" || !!user?.manages;
  const isAdmin = user?.role === "admin";

  const loadMy = async () => {
    try {
      const [t, m] = await Promise.all([api.get("/attendance/today"), api.get("/attendance/my")]);
      setToday(t.data); setMy(m.data);
      setTg(t.data?.tasks_given || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (user?.employee_id) loadMy(); }, [user]);

  // countdown for undo window
  useEffect(() => {
    if (!today?.check_out) { setUndoLeft(0); return; }
    const co = dayjs(today.check_out);
    const tick = () => {
      const remaining = 15 * 60 - dayjs().diff(co, "second");
      setUndoLeft(remaining > 0 ? remaining : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [today?.check_out]);

  const checkIn = async () => {
    setBusy(true);
    try { await api.post("/attendance/check-in", {}); toast.success("Checked in"); loadMy(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const saveTasksGiven = async () => {
    if (!tg.trim()) { toast.error("Please describe your planned tasks"); return; }
    setBusy(true);
    try { await api.patch("/attendance/today/tasks", { tasks_given: tg }); toast.success("Today's tasks saved"); loadMy(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const checkOut = async () => {
    if (!tc.trim()) { toast.error("Please log your completed tasks before logging out"); return; }
    setBusy(true);
    try { await api.post("/attendance/check-out", { tasks_completed: tc }); toast.success("Checked out"); setTc(""); loadMy(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const undoCheckOut = async () => {
    setBusy(true);
    try { await api.post("/attendance/check-out/undo"); toast.success("Check-out reverted"); loadMy(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;
  const hasTasksGiven = !!(today?.tasks_given || "").trim();
  const undoMin = Math.floor(undoLeft / 60);
  const undoSec = undoLeft % 60;

  const anyAbsent = my.some((r) => r.status === "absent");

  return (
    <div className="space-y-6">
      {!embedded && (
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="mt-1 text-sm text-zinc-500">Daily task log — check-in, log your tasks, check-out.</p>
      </div>
      )}

      <Tabs defaultValue={user?.employee_id ? "my" : "team"}>
        <TabsList className="bg-transparent p-0 border-b border-zinc-200 rounded-none h-auto">
          {user?.employee_id && <TabsTrigger value="my" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-my-attendance">My attendance</TabsTrigger>}
          {isMgr && <TabsTrigger value="team" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-team-attendance">Team log</TabsTrigger>}
            {isAdmin && <TabsTrigger value="monthly" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-monthly-attendance">Monthly</TabsTrigger>}
          {isAdmin && <TabsTrigger value="approvals" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-approvals">Approvals</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="my" className="mt-6 space-y-6">
            {/* Today card */}
            <Card className="border-zinc-200 shadow-none">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Today · {dayjs().format("D MMM YYYY")}</div>
                    <div className="mt-1 text-lg font-semibold">
                      {checkedOut ? `Done · ${today?.work_hours}h` : checkedIn ? `In at ${timeIso(today?.check_in)}` : "Not checked in"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={checkIn} disabled={busy || checkedIn} className="bg-emerald-600 hover:bg-emerald-700 press-scale" data-testid="attendance-check-in-btn">
                      <LogIn className="h-4 w-4 mr-2" /> Check in
                    </Button>
                    <Button onClick={checkOut} disabled={busy || !checkedIn || checkedOut || !hasTasksGiven || !tc.trim()} variant="outline" className="border-zinc-300 press-scale" data-testid="attendance-check-out-btn">
                      <LogOut className="h-4 w-4 mr-2" /> Check out
                    </Button>
                    {checkedOut && undoLeft > 0 && (
                      <Button onClick={undoCheckOut} disabled={busy} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 press-scale" data-testid="attendance-undo-btn">
                        <Undo2 className="h-4 w-4 mr-2" /> Undo ({undoMin}:{String(undoSec).padStart(2, "0")})
                      </Button>
                    )}
                    <RegularizeDialog onDone={loadMy} />
                  </div>
                </div>

                {checkedIn && !checkedOut && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4">
                    <Label className="text-xs font-medium text-emerald-800 flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> Today's tasks (planned)</Label>
                    <Textarea rows={3} value={tg} onChange={(e) => setTg(e.target.value)} placeholder="What are you working on today?" className="mt-2 bg-white" data-testid="tasks-given-input" />
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" onClick={saveTasksGiven} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tasks-given-save">
                        <Save className="h-3.5 w-3.5 mr-2" /> Save tasks
                      </Button>
                    </div>
                  </div>
                )}

                {checkedIn && !checkedOut && (
                  <div className="rounded-md border border-zinc-200 p-4">
                    <Label className="text-xs font-medium text-zinc-700">Tasks completed (required to check out)</Label>
                    <Textarea rows={3} value={tc} onChange={(e) => setTc(e.target.value)} placeholder="Summary of what you finished today" className="mt-2" data-testid="tasks-completed-input" />
                    {!hasTasksGiven && <p className="mt-1 text-[11px] text-amber-700">Save "Today's tasks" first before you can check out.</p>}
                  </div>
                )}

                {checkedOut && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Tasks given</div>
                      <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{today?.tasks_given || "—"}</p>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Tasks completed</div>
                      <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{today?.tasks_completed || "—"}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {anyAbsent && (
              <Card className="border-amber-200 shadow-none bg-amber-50/50">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                  <div className="flex-1 text-sm text-amber-900">
                    You have days flagged as <span className="font-semibold">Absent/Incomplete</span>. Request regularization to correct them.
                  </div>
                  <RegularizeDialog onDone={loadMy} />
                </CardContent>
              </Card>
            )}

            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">My log</CardTitle></CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Log in</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Task given</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Log out</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Task completed</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Hours</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {my.map((r) => (
                      <TableRow key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                        <TableCell className="text-sm">{dayjs(r.date).format("ddd, D MMM")}</TableCell>
                        <TableCell className="text-sm tabular-nums">{timeIso(r.check_in)}</TableCell>
                        <TableCell className="text-sm text-zinc-600 max-w-[220px] truncate" title={r.tasks_given || ""}>{r.tasks_given || "—"}</TableCell>
                        <TableCell className="text-sm tabular-nums">{timeIso(r.check_out)}</TableCell>
                        <TableCell className="text-sm text-zinc-600 max-w-[220px] truncate" title={r.tasks_completed || ""}>{r.tasks_completed || "—"}</TableCell>
                        <TableCell className="text-sm text-right tabular-nums">{r.work_hours || 0}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[11px] ${statusColor(r.status)}`}>{(r.status || "—").replace("_", " ")}</Badge></TableCell>
                      </TableRow>
                    ))}
                    {!my.length && <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 py-8">No records yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        )}

        {isMgr && <TabsContent value="team" className="mt-6"><TeamLog /></TabsContent>}
        {isAdmin && <TabsContent value="monthly" className="mt-6"><MonthlySummary /></TabsContent>}
        {isAdmin && <TabsContent value="approvals" className="mt-6"><ApprovalsPage embedded /></TabsContent>}
      </Tabs>
    </div>
  );
}

function TeamLog() {
  const [fromDate, setFromDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [empFilter, setEmpFilter] = useState("all");
  const [savingKey, setSavingKey] = useState(null);
  const [monthlyEdits, setMonthlyEdits] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/attendance/team", { params: { from_date: fromDate, to_date: toDate } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [fromDate, toDate]);

  const saveRemark = async (empId, date, val) => {
    const key = `${empId}|${date}`;
    setSavingKey(key);
    try {
      await api.patch(`/attendance/${empId}/${date}/remarks`, { remark: val });
      toast.success("Remark saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };
  const saveMonthlyRemark = async (empId) => {
    const val = monthlyEdits[empId] || "";
    const d = dayjs(fromDate);
    try {
      await api.post("/attendance/monthly-remark", { employee_id: empId, year: d.year(), month: d.month() + 1, remark: val });
      toast.success("Monthly remark saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  
  async function exportAttendance(full) {
    try {
      const params = { employee_id: empFilter };
      if (full) {
        params.full_history = true;
      } else {
        params.from_date = fromDate;
        params.to_date = toDate;
      }
      const res = await api.get("/attendance/export", { params, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fallbackName = full ? "attendance_full-history.xlsx" : `attendance_${fromDate}_${toDate}.xlsx`;
      const filename = match ? match[1] : fallbackName;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  const employees = data.employees.filter((e) => empFilter === "all" || e.employee.id === empFilter);

  return (
    <div className="space-y-4">
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 mt-1" data-testid="team-from-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 mt-1" data-testid="team-to-date" />
          </div>
          <div className="min-w-[220px]">
            <Label className="text-xs">Employee</Label>
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger className="h-9 mt-1" data-testid="team-emp-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {data.employees.map((e) => <SelectItem key={e.employee.id} value={e.employee.id}>{e.employee.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="border-zinc-300 press-scale" onClick={() => exportAttendance(false)} data-testid="export-period-btn">
            <Download className="h-4 w-4 mr-1" /> Export Selected Period
          </Button>
          <Button type="button" variant="outline" size="sm" className="border-zinc-300 press-scale" onClick={() => exportAttendance(true)} data-testid="export-full-history-btn">
            <Download className="h-4 w-4 mr-1" /> Export Full History
          </Button>
        </div>
      </CardContent>
      </Card>

      {employees.map((block) => (
        <Card key={block.employee.id} className="border-zinc-200 shadow-none" data-testid={`team-block-${block.employee.employee_code}`}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base font-semibold">{block.employee.name} <span className="text-xs text-zinc-500 font-normal">· {block.employee.employee_code}</span></CardTitle>
              <div className="mt-1 flex gap-4 text-xs text-zinc-500">
                <span>Present <span className="text-emerald-700 font-semibold">{block.summary.total_present_days}</span></span>
                <span>Half <span className="text-amber-700 font-semibold">{block.summary.total_half_days}</span></span>
                <span>Absent <span className="text-red-700 font-semibold">{block.summary.total_absent_days}</span></span>
                <span>Hours <span className="text-zinc-800 font-semibold tabular-nums">{block.summary.total_hours}</span></span>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[11px] text-zinc-500">Monthly remark ({dayjs(fromDate).format("MMM YYYY")})</Label>
                <Input
                  value={monthlyEdits[block.employee.id] ?? ""} onChange={(e) => setMonthlyEdits({ ...monthlyEdits, [block.employee.id]: e.target.value })}
                  onBlur={() => saveMonthlyRemark(block.employee.id)}
                  className="h-8 w-72 mt-1" placeholder="e.g., met all monthly goals" data-testid={`monthly-remark-${block.employee.employee_code}`}
                />
              </div>
            </div>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Log in</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Task given</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Log out</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Task completed</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {block.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 py-6">No rows in this range.</TableCell></TableRow>}
                {block.rows.map((r) => (
                  <TableRow key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                    <TableCell className="text-sm">{dayjs(r.date).format("ddd, D MMM")}</TableCell>
                    <TableCell className="text-sm tabular-nums">{timeIso(r.check_in)}</TableCell>
                    <TableCell className="text-sm text-zinc-600 max-w-[200px] truncate" title={r.tasks_given || ""}>{r.tasks_given || "—"}</TableCell>
                    <TableCell className="text-sm tabular-nums">{timeIso(r.check_out)}</TableCell>
                    <TableCell className="text-sm text-zinc-600 max-w-[200px] truncate" title={r.tasks_completed || ""}>{r.tasks_completed || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[11px] ${statusColor(r.status)}`}>{(r.status || "—").replace("_", " ")}</Badge></TableCell>
                    <TableCell>
                      <Input
                        defaultValue={r.remarks || ""}
                        onBlur={(e) => e.target.value !== (r.remarks || "") && saveRemark(block.employee.id, r.date, e.target.value)}
                        className="h-8 min-w-[180px]"
                        placeholder="Add a note…"
                        data-testid={`remark-${block.employee.employee_code}-${r.date}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function RegularizeDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: dayjs().format("YYYY-MM-DD"), check_in: "09:30", check_out: "18:30", tasks_given: "", tasks_completed: "", reason: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/attendance/regularize", form); toast.success("Request submitted"); setOpen(false); onDone?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-zinc-300 press-scale" data-testid="regularize-btn"><Clock className="h-4 w-4 mr-2" /> Regularize</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0"><DialogTitle className="font-display text-xl">Request regularization</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Log in</Label><Input type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Log out</Label><Input type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="h-9" /></div>
            </div>
            <div><Label className="text-xs">Task given</Label><Textarea rows={2} value={form.tasks_given} onChange={(e) => setForm({ ...form, tasks_given: e.target.value })} data-testid="reg-tasks-given" /></div>
            <div><Label className="text-xs">Task completed</Label><Textarea rows={2} value={form.tasks_completed} onChange={(e) => setForm({ ...form, tasks_completed: e.target.value })} data-testid="reg-tasks-completed" /></div>
            <div><Label className="text-xs">Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required data-testid="regularize-reason" /></div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-100 bg-white shrink-0">
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="regularize-submit">Submit request</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// --- Monthly attendance -----------------------------------------------------
// The payroll-facing count. Every date in the month resolves to exactly one
// state on the backend, so these numbers are the ones payroll can act on.

const DAY_STYLE = {
  present: "bg-emerald-100 text-emerald-800",
  half_day: "bg-amber-100 text-amber-800",
  leave: "bg-sky-100 text-sky-800",
  missing: "bg-red-100 text-red-800",
  weekly_off: "bg-zinc-100 text-zinc-400",
  saturday: "bg-zinc-50 text-zinc-400",
  holiday: "bg-violet-100 text-violet-700",
  not_applicable: "bg-white text-zinc-300",
};
const DAY_LABEL = {
  present: "Present", half_day: "Half day", leave: "Leave", missing: "Missing",
  weekly_off: "Sunday", saturday: "Saturday", holiday: "Holiday",
  not_applicable: "Not employed",
};

function Tile({ label, value, tone }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${tone || "border-zinc-200 bg-white"}`}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function MonthlySummary() {
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/attendance/monthly-summary", { params: { month } });
      setData(res.data);
    } catch (e) { toast.error(formatApiError(e)); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/attendance/monthly-summary/export",
                                { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const t = data?.totals;
  const rows = data?.employees || [];

  return (
    <div className="space-y-5" data-testid="monthly-attendance">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-zinc-500">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="w-44" data-testid="monthly-month" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!rows.length}
                data-testid="monthly-export">Export</Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Employees" value={t.employees} />
          <Tile label="Need chasing" value={t.needs_chasing}
                tone={t.needs_chasing ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"} />
          <Tile label="Missing days" value={t.missing_days}
                tone={t.missing_days ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"} />
          <Tile label="Worked off-days" value={t.extra_days} />
        </div>
      )}

      {data && !data.holidays.length && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No holidays are listed for {dayjs(month + "-01").format("MMMM YYYY")}. If there
          should be one, add it to the holiday calendar first — every unlisted holiday is
          counted as a working day and shows up as a missing day here.
        </div>
      )}

      <div className="rounded-lg border border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Working</TableHead>
              <TableHead className="text-right">Present</TableHead>
              <TableHead className="text-right">Leave</TableHead>
              <TableHead className="text-right">Half</TableHead>
              <TableHead className="text-right">Missing</TableHead>
              <TableHead className="text-right">Extra</TableHead>
              <TableHead className="text-right">Payable</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-8">
                {loading ? "Loading…" : "Nobody was employed in this month."}
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.employee_id}>
                <TableRow
                  className={`cursor-pointer ${r.missing ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50"}`}
                  onClick={() => setOpenId(openId === r.employee_id ? null : r.employee_id)}
                  data-testid={`monthly-row-${r.employee_id}`}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">{r.employee_code || "—"}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.working_days}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.present}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.leave}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.half_day}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.missing ? <Badge className="bg-red-50 text-red-700 border-red-200">{r.missing}</Badge> : "0"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-zinc-500">{r.extra || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{r.payable_days}</TableCell>
                </TableRow>
                {openId === r.employee_id && (
                  <TableRow><TableCell colSpan={8} className="bg-white p-4">
                    <div className="flex flex-wrap gap-1">
                      {r.days.map((d) => (
                        <div key={d.date}
                             title={`${dayjs(d.date).format("ddd D MMM")} — ${DAY_LABEL[d.state] || d.state}`}
                             className={`w-9 rounded text-center text-[11px] py-1 ${DAY_STYLE[d.state] || "bg-zinc-100"}`}>
                          {dayjs(d.date).format("D")}
                        </div>
                      ))}
                    </div>
                    {!!r.missing_dates.length && (
                      <div className="mt-3 text-xs text-zinc-600">
                        <span className="font-medium text-red-700">Missing:</span>{" "}
                        {r.missing_dates.map((d) => dayjs(d).format("D MMM")).join(", ")}
                        {!!r.never_marked.length && (
                          <span className="text-zinc-500"> · {r.never_marked.length} never marked, {r.marked_absent.length} marked absent</span>
                        )}
                      </div>
                    )}
                    {!!r.extra_dates.length && (
                      <div className="mt-1 text-xs text-zinc-600">
                        <span className="font-medium">Worked off-days:</span>{" "}
                        {r.extra_dates.map((d) => dayjs(d).format("D MMM")).join(", ")}
                      </div>
                    )}
                  </TableCell></TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
